import React, { useState } from "react";
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddReply from "../../Modals/addReply";
import DeleteConfirmModal from "../../Modals/deleteConfirmModal";
import { showError, showToast } from "../../constants/toasts";
import { useDeleteCustomerSupportMutation } from "../../services/customerSupport";
import { CustomerSupport } from "../../types/General";
import IfCan from "../../components/IfCan";


type ModuleName =
  | "Dashboard"
  | "Notification Management"
  | "Manage Customer Support"
  | "Settings"
  | "Manage Customers"
  | "Manage Analytics"
  | "Manage CMS"
  | "Manage Subscriptions"
  | "Manage SubAdmin"
  | "Budget Intelligence"
  | "Document Processing"
  | "Hydration And Goal"
  | "Label Recognition"
  | "Location Intelligence"
  | "Manage Revenue"
  | "Shared Account"
  | "Shopping Meals"
  | "Translation Messaging"
  | "Reminder Engine"
  | "AI Behavior Language";

interface CustomerSupportListProps {
  status: "pending" | "resolved";
  data: CustomerSupport[];
  isLoading?: boolean;
  onSuccess?: () => void;
}

const CustomerSupportList: React.FC<CustomerSupportListProps> = ({
  status,
  data,
  isLoading = false,
  onSuccess,
}) => {
  const navigate = useNavigate();

  const [selectField1, setSelectField1] = useState("default");
  const handleChange1 = (event: SelectChangeEvent) => {
    setSelectField1(event.target.value as string);
  };

  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CustomerSupport | null>(
    null
  );
  const [deleteSupport] = useDeleteCustomerSupportMutation();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedItem(null);
  };

  const handleReply = (item: CustomerSupport) => {
    setSelectedItem(item);
    setOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    try {
      await deleteSupport({ id: selectedId }).unwrap();
      showToast("Query deleted successfully");
      onSuccess?.();
    } catch (e: any) {
      showError(e?.data?.message || "Failed to delete query");
    }
    setDeleteOpen(false);
    setSelectedId(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  

  return (
    <>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Owner Detail</TableCell>
              <TableCell>Query Title</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography>Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : data && data.length > 0 ? (
              data.map((item) => (
                <TableRow hover key={item.id}>
                  <TableCell>
                    <div
                      className="user_block click_action"
                      onClick={() => navigate("/manage-users/detail")}
                    >
                      <figure>
                        <img
                          src="/static/images/user_placeholder.png"
                          alt=""
                          width={40}
                        />
                      </figure>
                      <p>
                        <b>{item.user?.name || "N/A"}</b>
                        <span>{item.user?.email || "N/A"}</span>
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>
                      {item.query_title || "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>{item.subject || "N/A"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        maxWidth: "200px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.description || "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {item.created_at ? formatDate(item.created_at) : "N/A"}
                  </TableCell>
                  <TableCell>
                    <div className="table_actions v2">
                      <IfCan module="Manage Customer Support" action="add_edit">
                        <Box>
                          <IconButton onClick={() => handleReply(item)}>
                            <img
                              src="/static/images/reply_icon.svg"
                              alt="Reply"
                            />
                          </IconButton>
                        </Box>
                      </IfCan>
                      <IfCan module="Manage Customer Support" action="delete">
                        <Box>
                          <IconButton onClick={() => { setSelectedId(item.id); setDeleteOpen(true); }}>
                            <img
                              src="/static/images/trash2_icon.svg"
                              alt="Delete"
                            />
                          </IconButton>
                        </Box>
                      </IfCan>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography>No data available</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <IfCan module="Manage Customer Support" action="add_edit">
        <AddReply
          open={open}
          onClose={handleCloseModal}
          setOpen={setOpen}
          status={status}
          editId={selectedItem?.id || null}
          onSuccess={onSuccess}
        />
      </IfCan>

      <IfCan module="Manage Customer Support" action="delete">
        <DeleteConfirmModal
          open={deleteOpen}
          setOpen={setDeleteOpen}
          handleDelete={handleDelete}
          title="Are you sure you want to delete this query?"
          message="This action cannot be undone."
        />
      </IfCan>
    </>
  );
};

export default CustomerSupportList;
